import { atom, useAtomValue } from 'jotai';
import { useRef, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { isNil } from 'lodash-es';
import { useAtomCallback } from 'jotai/utils';

const layerLocksAtom = atom<number[]>([]);
const layerLocksState = atom(
  (get) => get(layerLocksAtom),
  (_, set, next: number[]) =>
    set(
      layerLocksAtom,
      next.slice(0).sort((a, b) => b - a),
    ),
);

const conflicts = {} as { [x: number]: string };
/**
 * 用于管理应用内各浮层的展示优先顺序、
 *
 * 建议lock机制，类z-index逻辑，数值越大越优先，但使用时要注意释放
 * @param key // string
 * @param initLock // 等效 z-index，只接受初始赋值，后续变动会忽略
 * @param autoLock 默认false, 为ture时会在mount/unmount阶段自动申请锁/释放锁，开启时要注意理解清楚mount/unmount
 * @returns
 */
const useLayerLock = (key: string, initLock: number, autoLock = false) => {
  const lockRef = useRef(initLock);
  const layerLocks = useAtomValue(layerLocksState);

  const isAcq = useMemo(() => {
    const cur = layerLocks[0];
    if (isNil(cur)) {
      return false;
    }
    if (lockRef.current >= cur) {
      return true;
    }
    return false;
  }, [layerLocks]);

  const release = useAtomCallback(
    useCallback((get, set) => {
      delete conflicts[lockRef.current];
      const layerLocks = get(layerLocksState);
      set(
        layerLocksState,
        layerLocks.filter((t) => t !== lockRef.current),
      );
    }, []),
  );

  const acquire = useAtomCallback(
    useCallback(
      (get, set) => {
        const ck = lockRef.current;
        if (conflicts[ck]) {
          const conflictKey = conflicts[ck];
          if (import.meta.env.DEV) {
            // 注意：只在开发状态下开启此提醒，但是开发时可能会因为hmr 导致错误冲突提示
            message.error(
              `<useLayerLock>: 值冲突：${conflictKey} ${key}, 该次锁定无效, 请注意不要使用相同的key或lock值进行acquire`,
            );
            // throw new Error(
            //   `<useLayerLock>: 该lock(${lockRef.current})值已被key：${conflictKey}占用`,
            // );
          }
          // eslint-disable-next-line no-console
          console.error(
            `<useLayerLock>: 值冲突：${conflictKey} ${key}, 该次锁定无效, 请注意不要使用相同的key或lock值进行acquire`,
          );
        } else {
          conflicts[ck] = key;
        }
        const layerLocks = get(layerLocksState);
        set(layerLocksState, [...layerLocks, lockRef.current]);
      },
      [key],
    ),
  );

  useEffect(() => {
    if (autoLock) {
      acquire();
      return () => {
        release();
      };
    }
    return () => undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { gained: isAcq, release, acquire };
};

export default useLayerLock;
